import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Youtube, ArrowRight, MapPin, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Telegram username
const TELEGRAM_USERNAME = 'jqf_info';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      toast.success(data.message);
      setEmail('');
    } catch (error) {
      toast.error('Error al suscribirse');
    } finally {
      setLoading(false);
    }
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/jonquintanophoto', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/jonquintanoguillate', label: 'Facebook' },
    { icon: Youtube, href: 'https://www.youtube.com/@JonQuintanoFotografia', label: 'YouTube' },
  ];

  return (
    <footer className="bg-card border-t border-border" data-testid="footer">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="block">
              {/* Logo claro (para tema claro) */}
              <img 
                src="https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/t69v7woh_jonq_logo.png"
                alt="Jon Quintano Photography"
                className="h-14 w-auto dark:hidden block"
              />
              {/* Logo oscuro (para tema oscuro) */}
              <img 
                src="https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/6hee9rk4_mio_logo_dark.png"
                alt="Jon Quintano Photography"
                className="h-14 w-auto dark:block hidden"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Fotografía profesional que captura momentos únicos con un enfoque artístico y emotivo.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  className="w-10 h-10 border border-border rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-xs uppercase tracking-widest font-medium">Enlaces</h4>
            <nav className="flex flex-col gap-3">
              <a href="/#portfolio" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Portfolio</a>
              <a href="/#servicios" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Servicios</a>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Tarifas</Link>
              <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
              <a href="/#contacto" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contacto</a>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-6">
            <h4 className="text-xs uppercase tracking-widest font-medium">Contacto</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Gamonal<br />
                  09007 Burgos, España
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <a href="mailto:info@jonquintano.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  info@jonquintano.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <a 
                  href={`https://t.me/${TELEGRAM_USERNAME}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  @{TELEGRAM_USERNAME}
                </a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-6">
            <h4 className="text-xs uppercase tracking-widest font-medium">Newsletter</h4>
            <p className="text-sm text-muted-foreground">
              Suscríbete para recibir las últimas novedades y trabajos.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent border-b border-input rounded-none px-0 focus-visible:ring-0"
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                variant="ghost" 
                size="icon"
                disabled={loading}
                data-testid="newsletter-submit"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Jon Quintano Fotografía. Todos los derechos reservados.
          </p>
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/privacidad" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Política de Privacidad
            </Link>
            <Link to="/politica-cookies" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Política de Cookies
            </Link>
            <Link to="/aviso-legal" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Aviso Legal
            </Link>
            <Link to="/admin/login" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Administración
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
